import { AccessTime, AddRounded, ArrowRight, Delete } from '@mui/icons-material'
import LoadingButton from '@mui/lab/LoadingButton'
import Timeline from '@mui/lab/Timeline'
import TimelineConnector from '@mui/lab/TimelineConnector'
import TimelineContent from '@mui/lab/TimelineContent'
import TimelineDot from '@mui/lab/TimelineDot'
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem'
import TimelineSeparator from '@mui/lab/TimelineSeparator'
import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Typography,
} from '@mui/material'
import { AddressDetailDisplay } from 'components/Common/AddressDisplay/AddressDisplay'
import AddressSearch from 'components/Common/AddressSearch/AddressSearch'
import moment from 'moment'
import { useMemo } from 'react'
import { Route } from 'react-router-dom'

const JourneyDetailsForm = ({
  preparedQuote,
  currentBooking,
  isQuoteRefetching,
  quoteRefetch,
  editStates,
  pickup,
  dropOff,
  setPickup,
  setDropOff,
  setAdditionalStops,
  updateStops,
  validStops,
  onJourneyApplied,
}: any) => {
  const addressSearchStyles = useMemo(
    () => ({
      control: (provided: any) => ({
        ...provided,
        height: 56,
        display: 'flex',
        justifyContent: 'flex-start',
      }),
      container: (provided: any) => ({
        ...provided,
        flexGrow: '1',
      }),
      placeholder: (provided: any) => ({
        ...provided,
        textAlign: 'left',
        whiteSpace: 'nowrap',
        maxWidth: '80%',
        textOverflow: 'ellipsis',
      }),
      singleValue: (provided: any) => ({
        ...provided,
        textAlign: 'left',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '80%',
      }),
      input: (provided: any) => ({
        ...provided,
        textAlign: 'left',
      }),
      menu: (provided: any) => ({
        ...provided,
      }),
      menuList: (provided: any) => ({
        ...provided,
        overflowX: 'hidden',
        overflowY: 'auto',
        textAlign: 'left',
      }),
      option: (provided: any) => ({
        ...provided,
        textAlign: 'left',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }),
    }),
    [],
  )

  const populateFixedRoute = (fixedRoute: any) => {
    setPickup(fixedRoute.pickup)
    setAdditionalStops([])
    setDropOff(fixedRoute.dropOff)
  }

  if (!preparedQuote) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box
      component="div"
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '5px',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Grid container>
        <Grid
          item
          xs={12}
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            width: '50%',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ width: '100%' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  margin: '10px',
                  flexDirection: 'column',
                  gap: '5px',
                  flexGrow: 0.95,
                }}
              >
                {editStates.journey ? (
                  <>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                      }}
                    >
                      <Typography
                        sx={{
                          minWidth: '100px',
                          alignContent: 'flex-end',
                        }}
                      >
                        PICK UP:
                      </Typography>
                      <AddressSearch
                        onAddresssSelected={(location) => {
                          if (location['fixedRoute'] !== undefined) {
                            populateFixedRoute(location.fixedRoute)
                          } else {
                            setPickup(location)
                          }
                        }}
                        addressPlaceholder={
                          preparedQuote.outbound.pickup.formattedAddress
                        }
                        defaultValue={
                          preparedQuote.outbound.pickup.formattedAddress
                        }
                        clientId={currentBooking?.booking.client.clientId}
                        styles={addressSearchStyles}
                        name={'outbound.pickup'}
                      />
                    </Box>
                    {preparedQuote?.outbound.additionalStops?.map(
                      (stop, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                          }}
                        >
                          <Typography sx={{ minWidth: '100px' }}>
                            {`Stop #${index + 1}`}
                          </Typography>
                          <AddressSearch
                            onAddresssSelected={(location) => {
                              if (location['fixedRoute'] !== undefined) {
                                // For stops, we might not want to populate fixed routes
                                // or handle them differently
                                updateStops(index, {
                                  formattedAddress: location.formattedAddress,
                                  latitude: location.latitude,
                                  longitude: location.longitude,
                                })
                              } else {
                                updateStops(index, {
                                  formattedAddress: location.formattedAddress,
                                  latitude: location.latitude,
                                  longitude: location.longitude,
                                })
                              }
                            }}
                            addressPlaceholder={stop.formattedAddress}
                            clientId={currentBooking?.booking.client.clientId}
                            styles={addressSearchStyles}
                            name={`outbound.additionalStops[${index}]`}
                          />
                          <IconButton
                            onClick={() => {
                              setAdditionalStops((prev: any[]) =>
                                prev.filter((_, i) => i !== index),
                              )
                            }}
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      ),
                    )}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        minWidth: '20px',
                        position: 'relative',
                      }}
                    >
                      <Typography
                        sx={{
                          minWidth: '100px',
                          alignContent: 'flex-end',
                        }}
                      >
                        DROP OFF:
                      </Typography>
                      <AddressSearch
                        onAddresssSelected={(location) => {
                          if (location['fixedRoute'] !== undefined) {
                            populateFixedRoute(location.fixedRoute)
                          } else {
                            setDropOff(location)
                          }
                        }}
                        addressPlaceholder={
                          preparedQuote.outbound.dropOff.formattedAddress
                        }
                        defaultValue={
                          preparedQuote.outbound.dropOff.formattedAddress
                        }
                        clientId={currentBooking?.booking.client.clientId}
                        styles={addressSearchStyles}
                        name={'outbound.dropOff'}
                      />
                      <IconButton
                        onClick={() =>
                          setAdditionalStops((prev: any) => [
                            ...prev,
                            {
                              latitude: 0,
                              longitude: 0,
                              formattedAddress: '',
                            },
                          ])
                        }
                        sx={{
                          position: 'absolute',
                          top: -23,
                          right: -17,
                          zIndex: 2,
                          backgroundColor: '#F7BD38',
                          boxShadow: '0px 2px 4px rgba(0,0,0,0.2)',
                          transition: 'transform 0.1s, box-shadow 0.1s',
                          transitionDelay: '250ms',
                          '&:hover': {
                            backgroundColor: '#D9A032',
                            transform: 'translateY(-2px)',
                            boxShadow: '0px 4px 8px rgba(0,0,0,0.3)',
                            '& + .addStopLabel': {
                              opacity: 1,
                              transform: 'translateY(-50%) translateX(-10px)',
                            },
                          },
                          '&:active': {
                            transform: 'translateY(1px)',
                            boxShadow: '0px 1px 2px rgba(0,0,0,0.2)',
                          },
                        }}
                      >
                        <AddRounded sx={{ color: 'white' }} />
                      </IconButton>
                      <Box
                        className="addStopLabel"
                        sx={{
                          position: 'absolute',
                          top: '-5px',
                          right: '-190px',
                          transform: 'translateY(-50%) translateX(0)',
                          backgroundColor: '#F7BD38',
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          boxShadow: '0px 2px 4px rgba(0,0,0,0.2)',
                          opacity: 0,
                          transition:
                            'opacity 0.2s ease 0.3s, transform 0.2s ease 0.3s',
                          whiteSpace: 'nowrap',
                          fontSize: '0.875rem',
                          zIndex: 1,
                        }}
                      >
                        Add additional stop
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <LoadingButton
                        onClick={() => {
                          onJourneyApplied?.() // NEW: mark changedJourney
                          quoteRefetch()
                        }}
                        endIcon={<ArrowRight />}
                        loading={isQuoteRefetching}
                        disabled={
                          (!dropOff && !pickup && !validStops) ||
                          isQuoteRefetching
                        }
                        loadingPosition="end"
                        variant="contained"
                      >
                        <span>Apply</span>
                      </LoadingButton>
                    </Box>
                  </>
                ) : (
                  <Timeline
                    sx={{
                      [`& .${timelineItemClasses.root}:before`]: {
                        flex: 0,
                        padding: 0,
                      },
                      margin: 0,
                    }}
                  >
                    <TimelineItem>
                      <TimelineSeparator>
                        <TimelineDot color="success" />
                        <TimelineConnector />
                      </TimelineSeparator>
                      <TimelineContent>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 'bold',
                            textTransform: 'uppercase',
                          }}
                        >
                          {preparedQuote?.outbound.pickup?.formattedAddress
                            ? preparedQuote?.outbound.pickup?.formattedAddress
                            : AddressDetailDisplay({
                                address: preparedQuote?.outbound.pickup,
                              })}
                        </Typography>
                        <Typography variant="body2">
                          {preparedQuote?.outbound.pickup.formattedAddress
                            ? AddressDetailDisplay({
                                address: preparedQuote?.outbound.pickup,
                              })
                            : null}
                        </Typography>
                      </TimelineContent>
                    </TimelineItem>

                    {preparedQuote?.outbound.additionalStops?.map(
                      (stop: any) => {
                        return (
                          <TimelineItem key={stop.latitude + stop.longitude}>
                            <TimelineSeparator>
                              <TimelineDot />
                              <TimelineConnector />
                            </TimelineSeparator>
                            <TimelineContent>
                              <Typography variant="body1">
                                {`#${
                                  (preparedQuote?.outbound.additionalStops?.indexOf(
                                    stop,
                                  ) ?? -1) + 1
                                }: ${stop.formattedAddress}`}
                              </Typography>
                            </TimelineContent>
                          </TimelineItem>
                        )
                      },
                    )}

                    {preparedQuote?.outbound.dropOff?.formattedAddress && (
                      <TimelineItem>
                        <TimelineSeparator>
                          <TimelineDot color="error" />
                        </TimelineSeparator>
                        <TimelineContent>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 'bold',
                              textTransform: 'uppercase',
                            }}
                          >
                            {preparedQuote?.outbound.dropOff?.formattedAddress
                              ? preparedQuote?.outbound.dropOff
                                  ?.formattedAddress
                              : AddressDetailDisplay({
                                  address: preparedQuote?.outbound.dropOff,
                                })}
                          </Typography>
                          <Typography variant="body2">
                            {preparedQuote?.outbound.dropOff?.formattedAddress
                              ? AddressDetailDisplay({
                                  address: preparedQuote?.outbound.dropOff,
                                })
                              : null}
                          </Typography>
                        </TimelineContent>
                      </TimelineItem>
                    )}

                    {/* {currentBooking?.booking.journeyTypeName === 'Return' && (
                      <>
                        <Divider sx={{ marginBottom: '18px' }} />
                        <TimelineItem>
                          <TimelineSeparator>
                            <TimelineDot color="success" />
                            <TimelineConnector />
                          </TimelineSeparator>
                          <TimelineContent>
                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: 'bold',
                                textTransform: 'uppercase',
                              }}
                            >
                              {relatedJourney?.pickup.formattedAddress
                                ? relatedJourney?.pickup.formattedAddress
                                : AddressDetailDisplay({
                                    address: relatedJourney?.pickup,
                                  })}
                            </Typography>
                            <Typography variant="body2">
                              {relatedJourney?.pickup.formattedAddress
                                ? AddressDetailDisplay({
                                    address: relatedJourney?.pickup,
                                  })
                                : null}
                            </Typography>
                          </TimelineContent>
                        </TimelineItem>

                        {relatedJourney?.additionalStops?.map((stop: any) => {
                          return (
                            <>
                              <TimelineItem
                                key={stop.latitude + stop.longitude}
                              >
                                <TimelineSeparator>
                                  <TimelineDot variant="outlined" />
                                  <TimelineConnector />
                                </TimelineSeparator>
                                <TimelineContent>
                                  <Typography variant="body1">
                                    {`#${
                                      relatedJourney?.additionalStops.indexOf(
                                        stop,
                                      ) + 1
                                    }: ${stop.formattedAddress}`}
                                  </Typography>
                                </TimelineContent>
                              </TimelineItem>
                            </>
                          )
                        })}

                        <TimelineItem>
                          <TimelineSeparator>
                            <TimelineDot color="error" />
                          </TimelineSeparator>
                          <TimelineContent>
                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: 'bold',
                                textTransform: 'uppercase',
                              }}
                            >
                              {relatedJourney?.dropOff.formattedAddress
                                ? relatedJourney?.dropOff.formattedAddress
                                : AddressDetailDisplay({
                                    address: relatedJourney?.dropOff,
                                  })}
                            </Typography>
                            <Typography variant="body2">
                              {relatedJourney?.dropOff.formattedAddress
                                ? AddressDetailDisplay({
                                    address: relatedJourney?.dropOff,
                                  })
                                : null}
                            </Typography>
                          </TimelineContent>
                        </TimelineItem>
                      </>
                    )} */}
                  </Timeline>
                )}
              </Box>
            </Box>
            {!editStates.journey && (
              <Box
                sx={{
                  display: 'flex',
                  gap: '20px',
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  paddingLeft: '21px',
                  paddingY: '10px',
                }}
              >
                {currentBooking.booking &&
                  currentBooking.booking.journeyTypeName !== 'AsDirected' && (
                    <>
                      <Box sx={{ display: 'flex', gap: '8px' }}>
                        <AccessTime />
                        <Typography>
                          {moment
                            .duration(
                              currentBooking?.booking.estimatedJourneyTime,
                              'seconds',
                            )
                            .hours() > 0
                            ? `${moment
                                .duration(
                                  currentBooking?.booking.estimatedJourneyTime,
                                  'seconds',
                                )
                                .hours()} hours ${moment
                                .duration(
                                  currentBooking?.booking?.estimatedJourneyTime,
                                  'seconds',
                                )
                                .minutes()} minutes`
                            : `${moment
                                .duration(
                                  currentBooking?.booking.estimatedJourneyTime,
                                  'seconds',
                                )
                                .minutes()} minutes`}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: '8px' }}>
                        <Route />
                        <Typography>{`${currentBooking?.booking.estimatedJourneyDistance} miles`}</Typography>
                      </Box>
                    </>
                  )}
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  )
}

export default JourneyDetailsForm
